import { type FC, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';

import { getFullName } from '@/common/lib/getFullName';
import { LeagueClaimRewardContext } from '@/components/Modals/LeagueClaimReward/context';
import { StringMain, StringSmall } from '@/components/Typography/Typography';
import { FlagIcon } from '@/components/ui/Icons';
import { WaitInfo } from '@/components/ui/WaitInfo/WaitInfo';
import { selectLeagueByRank } from '@/redux/selectors/leagues';
import { useAppSelector } from '@/redux/store';

import ArenaBanner from '../ArenaBanner/ArenaBanner';
import { LeagueEventInfo } from '../LeagueEventInfo/LeagueEventInfo';
import { LeagueNavigation } from '../LeagueNavigation/LeagueNavigation';
import { LeagueWinners } from '../LeagueWinners/LeagueWinners';
import { useDevDecoration } from '../PositionItem/decorations/useDevDecoration';
import { PositionItem } from '../PositionItem/PositionItem';
import { TopItemRequirenments } from '../PositionItem/TopItemRequirenments';

import { positionByRank, text } from './config';

const GlobalLeagues: FC = () => {
  const [activeLeagueRank, setActiveLeagueRank] = useState<
    number | undefined
  >();
  const { setIsOpen, setMyRewards, setLeagueId, setLeagueShortInfo } =
    useContext(LeagueClaimRewardContext);
  const myUserId = useAppSelector((state) => state.user.user?.id);
  const devDecorationId = useDevDecoration();
  const myFirstName = useAppSelector((state) => state.user.user?.firstName);
  const myLastName = useAppSelector((state) => state.user.user?.lastName);
  const myUserName = useAppSelector((state) => state.user.user?.userName);
  const myAvatar = useAppSelector((state) => state.user.user?.photoUrl);
  const myLevel = useAppSelector((state) => state.user.user?.level);
  const activeLeague = useAppSelector((state) =>
    selectLeagueByRank(state, activeLeagueRank),
  );
  const { t } = useTranslation();
  const textForEmpryList = activeLeague?.event.active
    ? text.active
    : text.inactive;
  useEffect(() => {
    if (activeLeague && !!activeLeague.myRewards.length) {
      setLeagueId(activeLeague.id);
      setLeagueShortInfo({
        name: activeLeague.name,
        num: activeLeagueRank || 0,
      });
      setMyRewards(activeLeague.myRewards);
      setIsOpen(true);
    }
  }, [activeLeague]);

  const top10VisibleAfter =
    !!activeLeague &&
    !activeLeague.event.active &&
    activeLeague.event.interval - activeLeague.event.timeUntilNextOrStart <
      60 * 60 * 24;

  return (
    <div className="w-full h-full pt-4 flex flex-col">
      <ArenaBanner className="mb-3" />

      <LeagueNavigation
        activeLeague={activeLeague}
        activeLeagueRank={activeLeagueRank}
        setActiveLeagueRank={setActiveLeagueRank}
      />
      {activeLeague && !activeLeague.previousArenaCalculated && (
        <div className="w-full flex-1 flex items-center justify-center">
          <WaitInfo
            title={{ text: t('Arena completed') }}
            additionalInfo={{
              text: t('Calculating results and distributing rewards'),
              className: 'max-w-[12rem]',
            }}
            description={{
              text: t(
                'This may take a few minutes... Thanks for your patience!',
              ),
              className: 'max-w-[13rem]',
            }}
          />
        </div>
      )}
      {activeLeague && activeLeague.previousArenaCalculated && (
        <>
          <LeagueEventInfo
            isEventActive={activeLeague.event.active}
            players={activeLeague.total}
            timeToEnd={activeLeague.event.timeUntilNextOrStart}
            interval={activeLeague.event.interval}
            activeLeagueRank={
              activeLeague.prevLeagueId ? activeLeague.prevLeagueId + 1 : 1
            }
          />
          {top10VisibleAfter && activeLeague.top10Users.length > 0 && (
            <StringMain
              weight="bold"
              className="!leading-1.2 !text-white/60 text-center mb-4 mt-4 block"
            >
              {t('Winners of the past competition')}
            </StringMain>
          )}
          {activeLeague.top10Users.length === 0 &&
            activeLeague.event.active && (
              <div className="w-full flex flex-col items-center pt-11 px-3">
                <FlagIcon className="w-12 h-12 shrink-0 mb-4 text-white/40" />
                <StringMain weight="bold" className="!leading-1.2 mb-2">
                  {t(textForEmpryList.title)}
                </StringMain>
                <StringSmall
                  weight="regular"
                  className="!leading-1.3 !text-white/60 text-center"
                >
                  <Trans
                    i18nKey={textForEmpryList.description}
                    components={{ 1: <br /> }}
                  />
                </StringSmall>
              </div>
            )}
          {activeLeague.top10Users.length > 0 &&
            (top10VisibleAfter || activeLeague.event.active) && (
              <>
                <LeagueWinners
                  list={activeLeague.top10Users.filter(
                    ({ position }) => position < 4,
                  )}
                />
                <div
                  id="league-rank"
                  className="w-full flex flex-col gap-1 mb-1 relative"
                >
                  {activeLeague.top10Users?.map((props, index) => {
                    const rewards = activeLeague.rewards[index]
                      ? activeLeague.rewards[index].rewards
                      : undefined;
                    const isUserPosition = myUserId === props.id;

                    return (
                      rewards && (
                        <PositionItem
                          style={
                            isUserPosition
                              ? {
                                  top: 'var(--header-height)',
                                }
                              : undefined
                          }
                          className={cn(
                            isUserPosition && 'sticky bottom-0 z-[4]',
                          )}
                          isUserPosition={isUserPosition}
                          decorationId={
                            isUserPosition && devDecorationId
                              ? devDecorationId
                              : props.decorationId
                          }
                          key={props.id}
                          rewards={rewards}
                          {...props}
                        />
                      )
                    );
                  })}
                  {activeLeague.rewards
                    .filter(({ rank }) => rank > 20)
                    .map(({ rewards, rank }) => {
                      const position = positionByRank[rank];
                      const minTarget = activeLeague[position];
                      const item = position ? position.slice(3) : '';
                      const isOwnPosition =
                        activeLeague.position.toLowerCase() === position;
                      const myInfo = isOwnPosition
                        ? {
                            avatar: myAvatar,
                            userName: getFullName({
                              firstName: myFirstName,
                              lastName: myLastName,
                              userName: myUserName,
                            }),
                            level: myLevel,
                          }
                        : undefined;

                      return (
                        rewards &&
                        minTarget && (
                          <TopItemRequirenments
                            key={`rank-${rank}`}
                            minTarget={
                              isOwnPosition
                                ? activeLeague.starPoints
                                : minTarget
                            }
                            item={item}
                            style={
                              isOwnPosition
                                ? {
                                    top: 'var(--header-height)',
                                  }
                                : undefined
                            }
                            rewards={rewards}
                            className={cn(isOwnPosition && 'sticky bottom-0')}
                            myInfo={myInfo}
                          />
                        )
                      );
                    })}
                </div>
              </>
            )}
        </>
      )}
    </div>
  );
};

export { GlobalLeagues };
